import React from "react";
import Image, { StaticImageData } from "next/image";

import ImageDebGesturePointUp from "@/app/assets/images/deb/poses/deb-gesture-point-up.svg";
import ImageDebGesturePointDown from "@/app/assets/images/deb/poses/deb-gesture-point-down.svg";
import ImageDebGesturePointRight from "@/app/assets/images/deb/poses/deb-gesture-point-right.svg";
import ImageDebGesturePointDiagonal from "@/app/assets/images/deb/poses/deb-gesture-point-diagonal.svg";
import ImageDebGestureOops from "@/app/assets/images/deb/poses/deb-gesture-oops.svg";
import ImageDebMopeSad from "@/app/assets/images/deb/poses/deb-mope-sad.svg";
import ImageDebGestureHappy from "@/app/assets/images/deb/poses/deb-gesture-happy.svg";
import ImageDebGestureTaDa from "@/app/assets/images/deb/poses/deb-gesture-tada.svg";
import ImageDebGestureThinking from "@/app/assets/images/deb/poses/deb-gesture-thinking.svg";
import ImageDebGestureInsight from "@/app/assets/images/deb/poses/deb-gesture-insight.svg";
import ImageDebGestureWave from "@/app/assets/images/deb/poses/deb-gesture-wave.svg";
import ImageDebHoldingIcon from "@/app/assets/images/deb/onboarding/deb-holding-icon-red.svg";
import ImageDebJumpHappy from "@/app/assets/images/deb/poses/deb-jump-happy.svg";
import clsx from "clsx";

export type DebPose =
  | "pointUp"
  | "pointDown"
  | "pointRight"
  | "pointDiagonal"
  | "oops"
  | "sad"
  | "happy"
  | "thinking"
  | "insight"
  | "jumpHappy"
  | "wave"
  | "tada"
  | "holdingIcon";
export type DebSize = "small" | "regular" | "large" | "custom";

interface DebImageProps {
  component: React.FC<React.SVGProps<SVGSVGElement>>;
  src: StaticImageData;
  alt: string;
  priority?: boolean;
}

const debImageMap: Record<DebPose, DebImageProps> = {
  pointUp: { component: ImageDebGesturePointUp, src: ImageDebGesturePointUp, alt: "Deb pointing up", priority: true },
  pointDown: { component: ImageDebGesturePointDown, src: ImageDebGesturePointDown, alt: "Deb pointing down", priority: true },
  pointRight: { component: ImageDebGesturePointRight, src: ImageDebGesturePointRight, alt: "Deb pointing right", priority: true },
  pointDiagonal: { component: ImageDebGesturePointDiagonal, src: ImageDebGesturePointDiagonal, alt: "Deb pointing up-right", priority: true },
  oops: { component: ImageDebGestureOops, src: ImageDebGestureOops, alt: "Deb going oopsy", priority: true },
  sad: { component: ImageDebMopeSad, src: ImageDebMopeSad, alt: "Deb looking sad", priority: true },
  happy: { component: ImageDebGestureHappy, src: ImageDebGestureHappy, alt: "Deb looking happy", priority: true },
  tada: { component: ImageDebGestureTaDa, src: ImageDebGestureTaDa, alt: "Deb showing off", priority: true },
  thinking: { component: ImageDebGestureThinking, src: ImageDebGestureThinking, alt: "Deb looking thoughtful", priority: true },
  insight: { component: ImageDebGestureInsight, src: ImageDebGestureInsight, alt: "Deb as a lightbulb looking thoughtful", priority: true },
  jumpHappy: { component: ImageDebJumpHappy, src: ImageDebJumpHappy, alt: "Deb jumping happily", priority: true },
  wave: { component: ImageDebGestureWave, src: ImageDebGestureWave, alt: "Deb waving", priority: true },
  holdingIcon: { component: ImageDebHoldingIcon, src: ImageDebHoldingIcon, alt: "Deb holding a homescreen icon", priority: true },
};

interface PosingDebProps {
  debPose: DebPose;
  debSize?: DebSize;
  className?: string;
  width?: number;
  height?: number;
}

export default function PosingDeb({ debPose, debSize = "regular", className, width, height }: PosingDebProps) {
  const debImageData = debImageMap[debPose];

  const sizes = {
    small: {
      width: 45,
      height: 45,
    },
    regular: {
      width: 90,
      height: 90,
    },
    large: {
      width: 180,
      height: 180,
    },
    custom: {
      width: width || "auto",
      height: height || "auto",
    },
  }

  const sizeClass = {
    small: `w-[${sizes.small.width}px] h-[${sizes.small.height}px]`,
    regular: `w-[${sizes.regular.width}px] h-[${sizes.regular.height}px]`,
    large: `w-[${sizes.large.width}px] h-[${sizes.large.height}px]`,
    custom: width && height ? `w-[${width}px] h-[${height}px]` : "", // Custom size can be set via className
  }[debSize];

  return (
    <debImageData.component 
      className={clsx(sizeClass, className)}
      width={sizes[debSize].width}
      height={sizes[debSize].height}
    />
  );
}
